import axios from "axios";
import { UserSession } from "../user_session";
import * as Vars from "./Vars";

export class Apps {

  /**
   * Loads the available app clients for the given project id.
   * @param {number} project_id The project ID
   * @return {Promise}
   */
  static async load(project_id){
    return new Promise((resolve) => {
      axios({
        url: Vars.API_ENDPOINT + "/console_api/v1/app/",
        method: "GET",
        params: {
          _console_token: UserSession.token,
          project: project_id
        }
      }).then(function(data){
        resolve(data.data);
      });
    });
  }

  /**
   * Creates a new app client, to connect to the schmuckliCloud service.
   * @param {number} project_id The ID of the project, where the app should be created.
   * @param {string} name The new name for the app.
   */
  static async create(project_id, name){
    return new Promise((resolve) => {
      axios({
        url: Vars.API_ENDPOINT + "/console_api/v1/app/",
        method: "POST",
        data: {
          _console_token: UserSession.token,
          project: project_id,
          name: name
        }
      }).then(function(data){
        resolve(data.data);
      });
    });
  }

  /**
   * Updates an existing app with a new name.
   * @param {number} app_id The ID of the app, which should be updated.
   * @param {string} name The new name of the app.
   */
  static async edit(app_id, name){
    return new Promise((resolve) => {
      axios({
        url: Vars.API_ENDPOINT + "/console_api/v1/app/",
        method: "PUT",
        data: {
          _console_token: UserSession.token,
          id: app_id,
          name: name
        }
      }).then(function(data){
        resolve(data.data);
      });
    });
  }

  /**
   * Deletes an existing app from the project.
   * @param {number} app_id The app ID, which should be deleted.
   */
  static async delete(app_id){
    return new Promise((resolve) => {
      axios({
        url: Vars.API_ENDPOINT + "/console_api/v1/app/",
        method: "DELETE",
        data: {
          _console_token: UserSession.token,
          id: app_id
        }
      }).then(function(data){
        resolve(data.data);
      });
    });
  }

  /**
   * Just get a single app by its ID.
   * @param {number} app_id The app ID, which should be retrieven.
   */
  static async get(app_id){
    return new Promise((resolve) => {
      axios({
        url: Vars.API_ENDPOINT + "/console_api/v1/app/",
        method: "GET",
        params: {
          _console_token: UserSession.token,
          app: app_id
        }
      }).then(function(data){
        resolve(data.data);
      });
    });
  }

  /**
   * Regenerates the app secret.
   * @param {number} app_id The app ID, where the app secret should be regenerated.
   */
  static async regenerate(app_id){
    return new Promise((resolve) => {
      axios({
        url: Vars.API_ENDPOINT + "/console_api/v1/app/",
        method: "PUT",
        data: {
          _console_token: UserSession.token,
          id: app_id,
          regenerate: true
        }
      }).then(function(data){
        resolve(data.data);
      });
    });
  }

  /**
   * Loads the origins from the app.
   * @param {Number} app_id The id of the app
   */
  static loadOrigins(app_id) {
    return new Promise((resolve) => {
      axios({
        url: Vars.API_ENDPOINT + "/console_api/v1/app/",
        method: "GET",
        params: {
          _console_token: UserSession.token,
          app: app_id,
          function: "origins"
        }
      }).then(function(data){
        resolve(data.data);
      });
    });
  }

  /**
   * Assign a new origin to the app.
   * @param {Number} app_id The id of the app
   * @param {String} origin The new origin domain
   */
  static assignOrigin(app_id, origin) {
    return new Promise((resolve) => {
      axios({
        url: Vars.API_ENDPOINT + "/console_api/v1/app/",
        method: "POST",
        data: {
          _console_token: UserSession.token,
          app_id: app_id,
          origin: origin,
          function: "add_origin"
        }
      }).then(function(data){
        resolve(data.data);
      });
    });
  }

  /**
   * Removes an origin from the app.
   * @param {Number} app_id The id of the app
   * @param {Number} origin_id The id of the origin, which should be deleted
   */
  static removeOrigin(app_id, origin_id) {
    return new Promise((resolve) => {
      axios({
        url: Vars.API_ENDPOINT + "/console_api/v1/app/",
        method: "DELETE",
        data: {
          _console_token: UserSession.token,
          id: app_id,
          origin_id: origin_id,
          function: "delete_origin"
        }
      }).then(function(data){
        resolve(data.data);
      });
    });
  }

  /**
   * Activates a suggestion for the origin.
   * @param {Number} app_id The id of the app
   * @param {Number} origin_id The id of the origin, which should be activated.
   */
  static updateOrigin(app_id, origin_id) {
    return new Promise((resolve) => {
      axios({
        url: Vars.API_ENDPOINT + "/console_api/v1/app/",
        method: "PUT",
        data: {
          _console_token: UserSession.token,
          id: app_id,
          origin_id: origin_id,
          function: "activate_origin_suggestion"
        }
      }).then(function(data){
        resolve(data.data);
      });
    });
  }

  /**
   * Fetches the storage permission of the given app.
   * @param {int} project_id The id of the project.
   * @param {int} app_id The id of the app.
   */
  static getStoragePermissions(project_id, app_id) {
    return new Promise((resolve) => {
      axios({
        url: Vars.API_ENDPOINT + "/console_api/v1/app/",
        method: "GET",
        params: {
          _console_token: UserSession.token,
          app: app_id,
          project: project_id,
          function: "storage_permissions"
        }
      }).then(function(data){
        resolve(data.data);
      });
    });
  }


  static setStoragePermissions(app_id, p_insert, p_update, p_delete, p_get) {
    return new Promise((resolve) => {
      axios({
        url: Vars.API_ENDPOINT + "/console_api/v1/app/",
        method: "PUT",
        data: {
          _console_token: UserSession.token,
          id: app_id,
          function: "set_storage_permissions",
          set_insert: p_insert,
          set_update: p_update,
          set_delete: p_delete,
          set_get: p_get
        }
      }).then(function(data){
        resolve(data.data);
      });
    });
  }
}
